import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useBlockLayout, useTable } from 'react-table';
import {
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  List,
  WindowScroller,
} from 'react-virtualized';
import { injectIntl } from 'react-intl';

import intlShape from '@/shared/utils/intlShape';
import { defaultColumn, getColumns } from './InvoicingHistoryHelpers';

const getStyles = (props, align = 'left') => [
  props,
  {
    style: {
      textAlign: align,
    },
  },
];
const headerProps = (props, { column }) => getStyles(props, column.align);
const headerGroupProps = props => getStyles(props, 'center');

const cellProps = (props, { cell }) => getStyles(props, cell.column.align);

const cache = new CellMeasurerCache({
  defaultHeight: 37,
  minHeight: 37,
  fixedWidth: true,
});

const InvoicingHistoryVirtualTable = ({
  data,
  monthColumns,
  intl,
  columnStatus,
  showAssignments,
  totals,
}) => {
  const columns = useMemo(
    () => getColumns(intl, monthColumns, columnStatus, showAssignments, totals),
    [monthColumns, columnStatus, showAssignments, intl, totals],
  );
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    footerGroups,
    rows,
    prepareRow,
    totalColumnsWidth,
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
    },
    useBlockLayout,
  );

  const RenderRow = React.useCallback(
    ({ index, style, parent, key }) => {
      const row = rows[index];
      prepareRow(row);
      return (
        <CellMeasurer cache={cache} columnIndex={0} key={key} parent={parent} rowIndex={index}>
          <div
            {...row.getRowProps({
              style,
              className: 'tr',
            })}
          >
            {row.cells.map(cell => (
              <div {...cell.getCellProps(cellProps)} className="td">
                {cell.render('Cell')}
              </div>
            ))}
          </div>
        </CellMeasurer>
      );
    },
    [prepareRow, rows],
  );

  useEffect(() => {
    // Clear cached row heights when data changes
    cache.clearAll();
  }, [data]);

  const handleResize = useCallback(() => {
    // Clear cached row heights when window resizes
    cache.clearAll();
  }, []);

  return (
    <div {...getTableProps(props => [props, { className: 'table table-report' }])}>
      <div className="thead sticky-header">
        {headerGroups.map(headerGroup => (
          <div {...headerGroup.getHeaderGroupProps(headerGroupProps)} className="tr">
            {headerGroup.headers.map(column => (
              <div {...column.getHeaderProps(headerProps)} className="th">
                {column.render('Header')}
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className="tfoot">
        {footerGroups.map(group => (
          <div {...group.getFooterGroupProps(headerGroupProps)} className="tr bold">
            {group.headers.map(column => (
              <div {...column.getFooterProps(headerProps)} className="th">
                {column.render('Footer')}
              </div>
            ))}
          </div>
        ))}
      </div>
      <WindowScroller>
        {({ height, isScrolling, registerChild, scrollTop, onChildScroll }) => (
          <div {...getTableBodyProps()} className="tbody" ref={registerChild}>
            <List
              autoHeight
              height={height}
              isScrolling={isScrolling}
              rowCount={rows.length}
              rowHeight={cache.rowHeight}
              rowRenderer={RenderRow}
              onScroll={onChildScroll}
              scrollTop={scrollTop}
              width={totalColumnsWidth}
            />
          </div>
        )}
      </WindowScroller>
    </div>
  );
};
InvoicingHistoryVirtualTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  monthColumns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  columnStatus: PropTypes.shape({}).isRequired,
  totals: PropTypes.shape({}).isRequired,
  showAssignments: PropTypes.bool.isRequired,
  intl: intlShape.isRequired,
};
export default injectIntl(InvoicingHistoryVirtualTable);
